// MBA Lab — Coaching Question Deck UI. Imports only lib/core.js and
// lib/coaching-deck-model.js. Every question is server-rendered as static
// content (see Views/Tools/CoachingQuestionDeck.cshtml) so the full deck is
// readable and usable with JS disabled - this file only adds the save/build-
// a-set interactivity on top, matching the rest of this codebase's
// progressive-enhancement precedent (readiness, snapshot).
import { store, download, trackToolAction } from './lib/core.js';
import { hydrate, mountSyncIndicator, syncedStore } from './lib/account-sync.js';
import {
  SCHEMA_VERSION,
  LIMITS,
  createInitialState,
  parseSavedState,
  serializeForSave,
  toggleSaved,
  isSaved,
  isSavedFull,
  savedCount,
  clearSaved,
  getSavedQuestions,
  buildSetText,
} from './lib/coaching-deck-model.js';

const TOOL_NAME = 'coaching-question-deck';

function byId(id) {
  return document.getElementById(id);
}

function clearChildren(el) {
  while (el.firstChild) el.removeChild(el.firstChild);
}

class CoachingQuestionDeck {
  constructor() {
    this.cacheElements();
    // Signed in: also syncs to the account (see lib/account-sync.js). Signed out, or with
    // the feature off, this is a pure passthrough to the same localStorage wrapper.
    this.deckStore = syncedStore(TOOL_NAME, store(TOOL_NAME), {
      schemaVersion: SCHEMA_VERSION,
      onStatus: mountSyncIndicator(),
    });
    this.hasEngaged = false;

    this.state = this.loadInitialState();
    this.applyStateToButtons();
    this.renderSavedPanel();
    this.wireEvents();
    trackToolAction(TOOL_NAME, 'open');
  }

  cacheElements() {
    this.deck = byId('deck-questions');
    this.els = {
      savedCountText: byId('deck-saved-count'),
      savedEmpty: byId('deck-saved-empty'),
      savedList: byId('deck-saved-list'),
      savedActions: byId('deck-saved-actions'),
      clearBtn: byId('deck-clear'),
      copyBtn: byId('deck-copy'),
      downloadBtn: byId('deck-download'),
      actionFeedback: byId('deck-action-feedback'),
    };
  }

  loadInitialState() {
    const saved = this.deckStore.load();
    if (!saved) return createInitialState();
    const result = parseSavedState(saved);
    if (result.status === 'ok' || result.status === 'migrated') return result.state;
    return createInitialState();
  }

  wireEvents() {
    this.deck.addEventListener('click', (e) => this.handleDeckClick(e));
    this.els.savedList.addEventListener('click', (e) => this.handleSavedListClick(e));
    this.els.clearBtn.addEventListener('click', () => this.handleClear());
    this.els.copyBtn.addEventListener('click', () => this.handleCopy());
    this.els.downloadBtn.addEventListener('click', () => this.handleDownload());
  }

  handleDeckClick(e) {
    const btn = e.target.closest('[data-question-id]');
    if (!btn || !this.deck.contains(btn)) return;
    this.toggle(btn.dataset.questionId);
  }

  handleSavedListClick(e) {
    const btn = e.target.closest('[data-question-id]');
    if (!btn || !this.els.savedList.contains(btn)) return;
    this.toggle(btn.dataset.questionId);
  }

  toggle(id) {
    const wasSaved = isSaved(this.state, id);
    this.state = toggleSaved(this.state, id);
    if (!wasSaved && !isSaved(this.state, id)) return; // no-op: cap reached
    this.markEngaged();
    this.applyStateToButtons();
    this.renderSavedPanel();
    this.save();
  }

  markEngaged() {
    if (this.hasEngaged) return;
    this.hasEngaged = true;
    trackToolAction(TOOL_NAME, 'engage');
  }

  applyStateToButtons() {
    const full = isSavedFull(this.state);
    for (const btn of this.deck.querySelectorAll('[data-question-id]')) {
      const saved = isSaved(this.state, btn.dataset.questionId);
      btn.setAttribute('aria-pressed', String(saved));
      btn.textContent = saved ? 'Saved' : 'Save';
      btn.classList.toggle('is-saved', saved);
      btn.disabled = !saved && full;
    }
  }

  renderSavedPanel() {
    const saved = getSavedQuestions(this.state);
    this.els.savedCountText.textContent = `${savedCount(this.state)} of ${LIMITS.maxSaved} saved`;
    this.els.savedEmpty.hidden = saved.length > 0;
    this.els.savedActions.hidden = saved.length === 0;

    clearChildren(this.els.savedList);
    for (const q of saved) {
      const li = document.createElement('li');
      li.className = 'tool-deck-saved-item';

      const text = document.createElement('span');
      text.textContent = q.text;
      li.appendChild(text);

      const removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.className = 'tool-deck-saved-remove';
      removeBtn.dataset.questionId = q.id;
      removeBtn.setAttribute('aria-label', `Remove "${q.text}" from your set`);
      removeBtn.textContent = '×';
      li.appendChild(removeBtn);

      this.els.savedList.appendChild(li);
    }
  }

  save() {
    const toSave = { ...this.state, savedAt: Date.now() };
    const json = serializeForSave(toSave);
    if (!json) return;
    this.deckStore.save(toSave);
  }

  handleClear() {
    this.state = clearSaved(this.state);
    this.applyStateToButtons();
    this.renderSavedPanel();
    this.save();
  }

  handleDownload() {
    const text = buildSetText(this.state);
    if (!text) return;
    download('coaching-question-set.txt', 'text/plain', text);
    this.showActionFeedback('Downloaded.');
    trackToolAction(TOOL_NAME, 'download');
  }

  async handleCopy() {
    const text = buildSetText(this.state);
    if (!text) return;
    let copied = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        copied = true;
      }
    } catch {
      copied = false;
    }
    if (!copied) copied = this.fallbackCopy(text);

    this.showActionFeedback(copied ? 'Copied!' : 'Could not copy - try downloading instead.');
    if (copied) trackToolAction(TOOL_NAME, 'copy');
  }

  fallbackCopy(text) {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    let ok = false;
    try {
      ok = document.execCommand('copy');
    } catch {
      ok = false;
    }
    document.body.removeChild(textarea);
    return ok;
  }

  showActionFeedback(message) {
    this.els.actionFeedback.textContent = message;
    setTimeout(() => {
      if (this.els.actionFeedback.textContent === message) this.els.actionFeedback.textContent = '';
    }, 3000);
  }
}

document.addEventListener('DOMContentLoaded', async () => {
  if (!byId('deck-questions')) return;
  // Reconcile with the account BEFORE the tool reads its own storage, so it simply loads
  // whatever won. A no-op (and no request) when signed out, and bounded by its own short
  // timeout so a slow network delays startup rather than blocking it.
  await hydrate(TOOL_NAME, store(TOOL_NAME), SCHEMA_VERSION);
  new CoachingQuestionDeck();
});
